import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import analyticsService, { ConversionFunnelData } from '../../services/analyticsService';

const ConversionFunnel: React.FC = () => {
  const [data, setData] = useState<ConversionFunnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last30Days');

  const dateRanges = [
    { id: 'today', label: 'Today' },
    { id: 'last7Days', label: 'Last 7 Days' },
    { id: 'last30Days', label: 'Last 30 Days' },
    { id: 'last90Days', label: 'Last 90 Days' },
    { id: 'allTime', label: 'All Time' }
  ];

  const fetchFunnelData = useCallback(async () => {
    try {
      setLoading(true);
      const ranges = analyticsService.getPresetDateRanges();
      const currentRange = ranges[dateRange as keyof typeof ranges];
      const funnelData = await analyticsService.getConversionFunnel(currentRange);
      setData(funnelData);
    } catch (error) {
      console.error('Failed to fetch funnel data:', error);
      toast.error('Failed to load conversion funnel');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchFunnelData();
  }, [fetchFunnelData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No funnel data available</p>
      </div>
    );
  }

  const funnelSteps = [
    {
      label: 'Website Visitors',
      value: data.total_visitors,
      percentage: '100.0',
      color: 'bg-blue-500',
      description: 'Total unique visitors who viewed your website'
    },
    {
      label: 'Started Conversations',
      value: data.total_conversations,
      percentage: data.conversion_rates.visitor_to_conversation,
      color: 'bg-green-500',
      description: 'Visitors who initiated a chat conversation'
    },
    {
      label: 'Provided Name',
      value: data.conversations_with_name,
      percentage: data.conversion_rates.conversation_to_name,
      color: 'bg-yellow-500',
      description: 'Conversations where visitor provided their name'
    },
    {
      label: 'Provided Email',
      value: data.conversations_with_email,
      percentage: data.conversion_rates.name_to_email,
      color: 'bg-orange-500',
      description: 'Visitors who shared their email address'
    },
    {
      label: 'Became Leads',
      value: data.total_leads,
      percentage: data.conversion_rates.email_to_lead,
      color: 'bg-purple-500',
      description: 'Complete lead submissions with full information'
    },
    {
      label: 'Active Subscribers',
      value: data.subscribed_leads,
      percentage: data.conversion_rates.lead_to_subscribed,
      color: 'bg-teal-500',
      description: 'Leads who remain subscribed to email communications'
    }
  ];

  const maxValue = Math.max(...funnelSteps.map(step => step.value));

  return (
    <div className="space-y-6">
      {/* Header with Date Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Conversion Funnel Analysis</h3>
          <p className="text-gray-600 mt-1">Track the visitor journey from first contact to lead conversion</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="block w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {dateRanges.map((range) => (
              <option key={range.id} value={range.id}>
                {range.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="space-y-6">
          {funnelSteps.map((step, index) => {
            const width = maxValue > 0 ? (step.value / maxValue) * 100 : 0;
            const dropOffFromPrevious = index > 0 
              ? ((funnelSteps[index - 1].value - step.value) / funnelSteps[index - 1].value * 100).toFixed(1)
              : '0.0';

            return (
              <div key={index} className="relative">
                {/* Step Bar */}
                <div className="flex items-center mb-2">
                  <div className="w-full bg-gray-200 rounded-full h-8 relative overflow-hidden">
                    <div 
                      className={`h-full ${step.color} transition-all duration-500 ease-out flex items-center justify-between px-4`}
                      style={{ width: `${Math.max(width, 15)}%` }}
                    >
                      <span className="text-white font-medium text-sm truncate">
                        {step.label}
                      </span>
                      <span className="text-white font-bold text-sm">
                        {step.value.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Step Details */}
                <div className="flex items-center justify-between text-sm mb-4">
                  <div>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-4">
                      <span className="font-medium text-gray-900">
                        {step.percentage}% conversion
                      </span>
                      {index > 0 && (
                        <span className="text-red-600 text-xs">
                          -{dropOffFromPrevious}% drop-off
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Arrow between steps */}
                {index < funnelSteps.length - 1 && (
                  <div className="flex justify-center mb-4">
                    <div className="text-gray-400">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-blue-900 mb-2">Overall Conversion Rate</h4>
          <p className="text-3xl font-bold text-blue-700">{data.conversion_rates.overall_conversion}%</p>
          <p className="text-blue-600 text-sm mt-1">Visitors who became leads</p>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-green-900 mb-2">Engagement Rate</h4>
          <p className="text-3xl font-bold text-green-700">{data.conversion_rates.visitor_to_conversation}%</p>
          <p className="text-green-600 text-sm mt-1">Visitors who started conversations</p>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-purple-900 mb-2">Qualification Rate</h4>
          <p className="text-3xl font-bold text-purple-700">{data.conversion_rates.email_to_lead}%</p>
          <p className="text-purple-600 text-sm mt-1">Email captures that became leads</p>
        </div>

        <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-teal-900 mb-2">Email Health Rate</h4>
          <p className="text-3xl font-bold text-teal-700">{data.conversion_rates.lead_to_subscribed}%</p>
          <p className="text-teal-600 text-sm mt-1">Leads who remain subscribed</p>
        </div>
      </div>

      {/* Insights and Recommendations */}
      <div className="bg-yellow-50 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-yellow-900 mb-3">💡 Optimization Insights</h4>
        <div className="space-y-2 text-yellow-800">
          {parseFloat(data.conversion_rates.visitor_to_conversation) < 10 && (
            <p>• Consider improving your chat widget visibility or welcome message to increase conversation starts</p>
          )}
          {parseFloat(data.conversion_rates.conversation_to_name) < 50 && (
            <p>• Work on building trust early in conversations to encourage name sharing</p>
          )}
          {parseFloat(data.conversion_rates.name_to_email) < 70 && (
            <p>• Focus on email collection strategies - consider offering valuable resources</p>
          )}
          {parseFloat(data.conversion_rates.email_to_lead) < 80 && (
            <p>• Improve the lead submission flow to reduce drop-offs at the final step</p>
          )}
          {parseFloat(data.conversion_rates.lead_to_subscribed) < 95 && (
            <p>• Email health: {data.unsubscribed_leads + data.bounced_leads} leads are unsubscribed/bounced - review email content and frequency</p>
          )}
          {parseFloat(data.conversion_rates.lead_to_subscribed) >= 95 && (
            <p>• Excellent email health! Your leads are staying engaged with your communications</p>
          )}
          {parseFloat(data.conversion_rates.overall_conversion) > 5 && (
            <p>• Great job! Your overall conversion rate is performing well</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversionFunnel;